import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import MicNoneRoundedIcon from '@mui/icons-material/MicNoneRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import {
  getProspectLinkPagePreview,
  getProspectWhatsAppPreviewMessages,
  sendProspectWhatsAppPreviewMessage,
} from '../Products/FoodstationLinks/services/api';
import foodstationLogo from '../FoodstationLP/foodstation.png';
import './whatsapp-prospect-preview.css';

const FOODSTATION_CONTACT = {
  id: 'foodstation',
  name: 'FoodStation',
  avatar: foodstationLogo,
  avatarVariant: 'brand',
  emptyPreview: 'Converse com a FoodStation',
};

const FOODSTATION_GREETING =
  'Obrigado por testar! 😊 Na outra conversa você experimenta uma prévia de como vai funcionar o seu atendimento. Por aqui, qualquer dúvida sobre a FoodStation é só perguntar!';

const CONVERSATION_IDS = ['prospect', 'foodstation'];
const COMPOSER_HINT_DELAY_MS = 1800;
const FAKE_GREETING_DELAY_MS = 3000;
const POLL_INTERVAL_MS = 5000;
const MESSAGE_LINK_PATTERN = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<]+)/gi;

const renderEmphasis = (text) => String(text).split(/(\*\*[^*\n]+\*\*|\*[^*\n]+\*)/g)
  .filter(Boolean)
  .map((part, index) => {
    const markerLength = part.startsWith('**') ? 2 : 1;
    const isBold = part.startsWith('*') && part.endsWith('*')
      && part.length > markerLength * 2;
    return isBold
      ? <strong key={index}>{part.slice(markerLength, -markerLength)}</strong>
      : part;
  });

const renderMessageText = (text) => {
  const content = String(text || '');
  const parts = [];
  let lastIndex = 0;

  for (const match of content.matchAll(MESSAGE_LINK_PATTERN)) {
    if (match.index > lastIndex) {
      parts.push(<span key={`text-${lastIndex}`}>{renderEmphasis(content.slice(lastIndex, match.index))}</span>);
    }

    const label = match[1];
    const rawUrl = match[2] || match[3];
    const trailing = label ? '' : rawUrl.match(/[.,!?;:)\]]+$/)?.[0] || '';
    const url = trailing ? rawUrl.slice(0, -trailing.length) : rawUrl;
    parts.push(
      <a key={`link-${match.index}`} href={url} target="_blank" rel="noopener noreferrer">
        {renderEmphasis(label || url)}
      </a>,
    );
    if (trailing) parts.push(trailing);
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    parts.push(<span key={`text-${lastIndex}`}>{renderEmphasis(content.slice(lastIndex))}</span>);
  }
  return parts;
};

const createChatState = (clientId = '') => ({
  clientId,
  draft: '',
  error: '',
  loaded: false,
  messages: [],
  sending: false,
  unreadCount: 0,
});

const storageKeyFor = (prospectName, conversationType) => (
  `whatsapp_preview_chat:${String(prospectName || '').toLowerCase()}:${conversationType}`
);

const readStoredClientId = (prospectName, conversationType) => {
  try {
    return localStorage.getItem(storageKeyFor(prospectName, conversationType)) || '';
  } catch (_error) {
    return '';
  }
};

const storeClientId = (prospectName, conversationType, clientId) => {
  try {
    if (clientId) localStorage.setItem(storageKeyFor(prospectName, conversationType), clientId);
    else localStorage.removeItem(storageKeyFor(prospectName, conversationType));
  } catch (_error) {
  }
};

const formatMessageTime = (value) => {
  const parsed = value ? new Date(value) : new Date();
  if (Number.isNaN(parsed.getTime())) return 'agora';
  return parsed.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const mapMessages = (items = []) => (Array.isArray(items) ? items : [])
  .map((message) => ({
    id: message.id || `msg-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    side: message.from_bot || message.from_me ? 'incoming' : 'outgoing',
    text: message.message || '',
    time: formatMessageTime(message.date),
    read: !(message.from_bot || message.from_me),
    createdAt: message.date || new Date().toISOString(),
  }))
  .sort((first, second) => new Date(first.createdAt) - new Date(second.createdAt));

const createLocalMessage = ({ text, side, isFake = false }) => ({
  id: `${isFake ? 'fake' : 'local'}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  text,
  side,
  time: 'agora',
  read: side === 'outgoing',
  createdAt: new Date().toISOString(),
  isFake,
});

const initialsFor = (name) => String(name || 'FS')
  .trim()
  .split(/\s+/)
  .slice(0, 2)
  .map((word) => word[0])
  .join('')
  .toUpperCase();

const ContactAvatar = ({ src, name, size = 'regular', variant = 'avatar' }) => {
  const [failed, setFailed] = useState(false);
  return (
    <span
      className={`wpp-avatar wpp-avatar--${size} wpp-avatar--${variant}`}
      aria-hidden="true"
    >
      {src && !failed ? (
        <img src={src} alt="" onError={() => setFailed(true)} />
      ) : (
        <span>{initialsFor(name)}</span>
      )}
    </span>
  );
};

const ConversationItem = ({ contact, active, onSelect }) => (
  <button
    type="button"
    className={`wpp-conversation ${active ? 'is-active' : ''}`}
    aria-pressed={active}
    onClick={onSelect}
  >
    <ContactAvatar
      src={contact.avatar}
      name={contact.name}
      variant={contact.avatarVariant}
    />
    <span className="wpp-conversation-copy">
      <strong>{contact.name}</strong>
      <span>{contact.preview}</span>
    </span>
    {contact.badge ? <span className="wpp-badge">{contact.badge}</span> : null}
  </button>
);

const MessageBubble = ({ message }) => (
  <div className={`wpp-message-row wpp-message-row--${message.side}`}>
    <div className={`wpp-message wpp-message--${message.side}`}>
      <span className="wpp-message-text">{renderMessageText(message.text)}</span>
      <span className="wpp-message-meta">
        {message.time}
        {message.read ? <DoneAllRoundedIcon aria-label="Mensagem lida" /> : null}
      </span>
    </div>
  </div>
);

const TypingBubble = () => (
  <div className="wpp-message-row wpp-message-row--incoming">
    <div className="wpp-message wpp-message--incoming wpp-message--typing" aria-label="Digitando">
      <span /><span /><span />
    </div>
  </div>
);

const PreviewState = ({ children }) => (
  <main
    className="wpp-state-page"
    style={{ '--wpp-background-image': 'url("/background3.png")' }}
  >
    <div className="wpp-state-card">{children}</div>
  </main>
);

const WhatsAppProspectPreview = () => {
  const { prospectName } = useParams();
  const [prospect, setProspect] = useState(null);
  const [selectedId, setSelectedId] = useState('prospect');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showComposerHint, setShowComposerHint] = useState(false);
  const [composerHintDismissed, setComposerHintDismissed] = useState(false);
  const [mobileChatOpen, setMobileChatOpen] = useState(false);
  const [chats, setChats] = useState(() => ({
    prospect: createChatState(readStoredClientId(prospectName, 'prospect')),
    foodstation: createChatState(readStoredClientId(prospectName, 'foodstation')),
  }));
  const selectedIdRef = useRef(selectedId);
  const messageListRef = useRef(null);
  const composerInputRef = useRef(null);
  const prospectClientId = chats.prospect.clientId;
  const foodstationClientId = chats.foodstation.clientId;
  const foodstationChatLoaded = chats.foodstation.loaded;
  const foodstationMessageCount = chats.foodstation.messages.length;
  const selectedMessageCount = chats[selectedId].messages.length;
  const selectedSending = chats[selectedId].sending;

  const updateChat = (conversationType, updater) => {
    setChats((current) => ({
      ...current,
      [conversationType]: typeof updater === 'function'
        ? updater(current[conversationType])
        : { ...current[conversationType], ...updater },
    }));
  };

  useEffect(() => {
    selectedIdRef.current = selectedId;
    updateChat(selectedId, (current) => ({ ...current, unreadCount: 0 }));
  }, [selectedId]);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getProspectLinkPagePreview(prospectName);
        if (active) setProspect(data?.restaurant || null);
      } catch (requestError) {
        if (active) {
          setError(
            requestError?.response?.data?.error
            || 'Preview de conversas não encontrado.',
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    if (prospectName) void load();
    else {
      setLoading(false);
      setError('Preview de conversas não encontrado.');
    }

    return () => {
      active = false;
    };
  }, [prospectName]);

  useEffect(() => {
    let active = true;

    const loadConversation = async (conversationType, clientId) => {
      if (!clientId) {
        updateChat(conversationType, (current) => ({ ...current, loaded: true }));
        return;
      }

      try {
        const data = await getProspectWhatsAppPreviewMessages(
          prospectName,
          conversationType,
          clientId,
        );
        if (!active) return;
        const mapped = mapMessages(data?.mensagens);
        updateChat(conversationType, (current) => {
          const previousAssistantCount = current.messages.filter(
            (message) => message.side === 'incoming' && !message.isFake,
          ).length;
          const nextAssistantCount = mapped.filter((message) => message.side === 'incoming').length;
          const newUnread = selectedIdRef.current === conversationType
            ? 0
            : Math.max(0, nextAssistantCount - previousAssistantCount);
          return {
            ...current,
            loaded: true,
            messages: mapped.length ? mapped : current.messages,
            unreadCount: current.unreadCount + newUnread,
          };
        });
      } catch (requestError) {
        if (!active) return;
        if (requestError?.response?.status === 404) {
          storeClientId(prospectName, conversationType, '');
          updateChat(conversationType, (current) => ({
            ...current,
            clientId: '',
            loaded: true,
            messages: current.messages.filter((message) => message.isFake),
          }));
        } else {
          updateChat(conversationType, (current) => ({ ...current, loaded: true }));
        }
      }
    };

    const clientIds = {
      prospect: prospectClientId,
      foodstation: foodstationClientId,
    };
    const loadAll = () => Promise.all(CONVERSATION_IDS.map((conversationType) => (
      loadConversation(conversationType, clientIds[conversationType])
    )));

    void loadAll();
    const hasSession = Boolean(prospectClientId || foodstationClientId);
    const intervalId = hasSession ? window.setInterval(loadAll, POLL_INTERVAL_MS) : null;

    return () => {
      active = false;
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [foodstationClientId, prospectClientId, prospectName]);

  useEffect(() => {
    if (!foodstationChatLoaded || foodstationMessageCount > 0) return undefined;

    const timer = window.setTimeout(() => {
      updateChat('foodstation', (current) => {
        if (current.messages.length) return current;
        return {
          ...current,
          messages: [createLocalMessage({
            text: FOODSTATION_GREETING,
            side: 'incoming',
            isFake: true,
          })],
          unreadCount: selectedIdRef.current === 'foodstation' ? 0 : current.unreadCount + 1,
        };
      });
    }, FAKE_GREETING_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [foodstationChatLoaded, foodstationMessageCount]);

  useEffect(() => {
    if (loading || !prospect || selectedId !== 'prospect' || composerHintDismissed) {
      setShowComposerHint(false);
      return undefined;
    }

    const timer = window.setTimeout(() => setShowComposerHint(true), COMPOSER_HINT_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [composerHintDismissed, loading, prospect, selectedId]);

  useEffect(() => {
    const messageList = messageListRef.current;
    if (messageList) messageList.scrollTop = messageList.scrollHeight;
  }, [selectedMessageCount, selectedSending, selectedId]);

  const contacts = [
    {
      id: 'prospect',
      name: prospect?.name || 'Seu negócio',
      avatar: prospect?.profileImage || null,
      emptyPreview: 'Teste o atendimento do negócio',
    },
    FOODSTATION_CONTACT,
  ].map((contact) => ({
    ...contact,
    preview: chats[contact.id].messages[chats[contact.id].messages.length - 1]?.text
      || contact.emptyPreview,
    badge: chats[contact.id].unreadCount,
  }));
  const selectedContact = contacts.find((contact) => contact.id === selectedId) || contacts[0];
  const selectedChat = chats[selectedContact.id];

  const handleSend = async (event) => {
    event.preventDefault();
    composerInputRef.current?.focus();
    const conversationType = selectedContact.id;
    const chat = chats[conversationType];
    const message = chat.draft.trim();
    if (!message || chat.sending) return;

    updateChat(conversationType, (current) => ({
      ...current,
      draft: '',
      error: '',
      sending: true,
      messages: [
        ...current.messages.filter((item) => !item.isFake),
        createLocalMessage({ text: message, side: 'outgoing' }),
      ],
    }));

    try {
      const data = await sendProspectWhatsAppPreviewMessage(
        prospectName,
        conversationType,
        { clientId: chat.clientId || undefined, message },
      );
      const nextClientId = String(data?.clientId || chat.clientId || '');
      if (nextClientId) storeClientId(prospectName, conversationType, nextClientId);
      updateChat(conversationType, (current) => ({
        ...current,
        clientId: nextClientId,
        loaded: true,
        messages: mapMessages(data?.mensagens),
        sending: false,
      }));
    } catch (requestError) {
      updateChat(conversationType, (current) => ({
        ...current,
        error: requestError?.response?.data?.error
          || 'Não foi possível enviar a mensagem. Tente novamente.',
        sending: false,
      }));
    }
  };

  if (loading) return <PreviewState>Carregando conversas...</PreviewState>;
  if (error || !prospect) return <PreviewState>{error || 'Preview de conversas não encontrado.'}</PreviewState>;

  return (
    <main
      className="wpp-page"
      style={{ '--wpp-background-image': 'url("/background3.png")' }}
    >
      <section
        className={`wpp-app ${mobileChatOpen ? 'wpp-app--chat-open' : ''}`}
        aria-label="Prévia de conversas do WhatsApp"
      >
        <aside className="wpp-sidebar">
          <h1>Conversas</h1>
          <label className="wpp-search">
            <SearchRoundedIcon aria-hidden="true" />
            <input
              type="text"
              readOnly
              aria-label="Pesquisar conversas"
              placeholder="Pesquisar ou começar uma nova conversa"
            />
          </label>
          <div className="wpp-conversation-list" aria-label="Conversas disponíveis">
            {contacts.map((contact) => (
              <ConversationItem
                key={contact.id}
                contact={contact}
                active={selectedContact.id === contact.id}
                onSelect={() => {
                  setSelectedId(contact.id);
                  setMobileChatOpen(true);
                }}
              />
            ))}
          </div>
        </aside>

        <section className="wpp-chat" aria-label={`Conversa com ${selectedContact.name}`}>
          <header className="wpp-chat-header">
            <button
              type="button"
              className="wpp-chat-back"
              aria-label="Voltar para conversas"
              onClick={() => setMobileChatOpen(false)}
            >
              <ArrowBackRoundedIcon aria-hidden="true" />
            </button>
            <ContactAvatar
              src={selectedContact.avatar}
              name={selectedContact.name}
              size="small"
              variant={selectedContact.avatarVariant}
            />
            <div>
              <strong>{selectedContact.name}</strong>
              <span>{selectedChat.sending ? 'digitando…' : 'online'}</span>
            </div>
          </header>

          <div className="wpp-chat-body">
            <div className="wpp-day-label">Hoje</div>
            <div className="wpp-preview-notice">
              {selectedContact.id === 'foodstation'
                ? 'Tire todas as suas dúvidas sobre a FoodStation.'
                : 'Esta é uma demonstração de um atendimento.'}
            </div>
            <div className="wpp-message-list" aria-live="polite" ref={messageListRef}>
              {selectedChat.loaded && selectedChat.messages.length === 0 && (
                <div className="wpp-chat-empty">Envie uma mensagem para iniciar a conversa.</div>
              )}
              {selectedChat.messages.map((message) => (
                <MessageBubble key={message.id} message={message} />
              ))}
              {selectedChat.sending && <TypingBubble />}
            </div>

            {showComposerHint && selectedContact.id === 'prospect' ? (
              <div className="wpp-composer-hint" role="status">
                <span>
                  Mande uma mensagem para testar o atendimento automatizado feito pela IA.
                </span>
                <button
                  type="button"
                  className="wpp-composer-hint-close"
                  aria-label="Fechar dica de atendimento"
                  onClick={() => {
                    setShowComposerHint(false);
                    setComposerHintDismissed(true);
                  }}
                >
                  &times;
                </button>
              </div>
            ) : null}

            <form className="wpp-composer" aria-label="Compositor de mensagem" onSubmit={handleSend}>
              <input
                ref={composerInputRef}
                type="text"
                aria-label="Digite uma mensagem"
                placeholder="Digite uma mensagem"
                value={selectedChat.draft}
                maxLength={2000}
                onChange={(event) => {
                  const draft = event.target.value;
                  updateChat(selectedContact.id, (current) => ({ ...current, draft, error: '' }));
                }}
              />
              {selectedChat.draft.trim() ? (
                <button
                  type="submit"
                  className="wpp-composer-send"
                  disabled={selectedChat.sending}
                  aria-label="Enviar mensagem"
                >
                  <SendRoundedIcon aria-hidden="true" />
                </button>
              ) : (
                <MicNoneRoundedIcon aria-hidden="true" />
              )}
            </form>
            {selectedChat.error && (
              <div className="wpp-chat-error" role="alert">{selectedChat.error}</div>
            )}
          </div>
        </section>
      </section>
    </main>
  );
};

export default WhatsAppProspectPreview;
